/**
 * SeamBridgeMap — SVG visualization of the four seams in 𝓥 and the
 * bridge constructors that re-inhabit their lifts in 𝓔_I.
 *
 * Each seam is a coloured arc spanning the base; each bridge sits in
 * the total space directly above its seam. The admission seam carries
 * no bridge (it is rendered as a refusal slash).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import seamsData from "../data/seams.json" with { type: "json" };
import { panelTokens } from "@csk/demo-ui";

interface Bridge {
  readonly id: string;
  readonly label: string;
  readonly schemaId: string;
}

interface Seam {
  readonly id: string;
  readonly symbol: string;
  readonly label: string;
  readonly tagline: string;
  readonly bridge: Bridge | null;
  readonly accent: string;
}

const SEAMS: readonly Seam[] = seamsData.seams;

export interface SeamBridgeMapProps {
  readonly highlightedSeamId?: string | null;
  readonly onHighlight?: (id: string | null) => void;
  readonly width?: number;
  readonly height?: number;
}

export function SeamBridgeMap({
  highlightedSeamId = null,
  onHighlight,
  width = 760,
  height = 320,
}: SeamBridgeMapProps): React.JSX.Element {
  const padX = 60;
  const baseY = 240;
  const totalY = 90;
  const colCount = SEAMS.length;
  const stride = (width - 2 * padX) / colCount;

  const handleEnter = (id: string): void => {
    onHighlight?.(id);
  };
  const handleLeave = (): void => {
    onHighlight?.(null);
  };

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      className="block"
      style={{ maxWidth: width }}
      role="img"
      aria-label="Map of the four ILG seams and their bridge constructors"
    >
      <defs>
        <marker
          id="seammap-arrow"
          viewBox="0 -5 10 10"
          refX="10"
          refY="0"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M0,-5L10,0L0,5" fill={panelTokens.muted} />
        </marker>
      </defs>

      <text
        x={20}
        y={totalY - 30}
        fill={panelTokens.muted}
        fontSize={11}
        fontFamily={panelTokens.mono}
        letterSpacing="0.12em"
      >
        𝓔_I — bridges
      </text>
      <text
        x={20}
        y={baseY + 60}
        fill={panelTokens.muted}
        fontSize={11}
        fontFamily={panelTokens.mono}
        letterSpacing="0.12em"
      >
        𝓥 — seams (raw change classes)
      </text>

      {/* Base baseline */}
      <line
        x1={padX - 10}
        y1={baseY + 18}
        x2={width - padX + 10}
        y2={baseY + 18}
        stroke={panelTokens.border}
        strokeWidth={1}
      />

      {SEAMS.map((seam, i) => {
        const cx = padX + stride * (i + 0.5);
        const isHi = highlightedSeamId === seam.id;
        const accent = seam.accent;
        const bridgeLabel = seam.bridge?.label ?? "(no bridge — refuse)";

        return (
          <g
            key={seam.id}
            onMouseEnter={() => {
              handleEnter(seam.id);
            }}
            onMouseLeave={handleLeave}
            style={{ cursor: "default" }}
          >
            {/* Seam glyph in base */}
            <rect
              x={cx - 56}
              y={baseY - 4}
              width={112}
              height={44}
              rx={10}
              fill={isHi ? `${accent}28` : `${accent}10`}
              stroke={accent}
              strokeOpacity={isHi ? 0.9 : 0.5}
              strokeWidth={isHi ? 1.8 : 1}
              style={{ transition: "all 160ms ease" }}
            />
            <text
              x={cx}
              y={baseY + 14}
              textAnchor="middle"
              fill={accent}
              fontFamily={panelTokens.mono}
              fontSize={18}
              fontWeight={800}
              style={{ textShadow: `0 0 8px ${accent}55` }}
            >
              {seam.symbol}
            </text>
            <text
              x={cx}
              y={baseY + 30}
              textAnchor="middle"
              fill={panelTokens.muted}
              fontSize={9.5}
              fontFamily={panelTokens.mono}
              letterSpacing="0.05em"
            >
              {seam.id}
            </text>

            {/* Lift arrow */}
            <line
              x1={cx}
              y1={baseY - 4}
              x2={cx}
              y2={totalY + 34}
              stroke={seam.bridge ? accent : panelTokens.accentRed}
              strokeOpacity={isHi ? 0.95 : 0.55}
              strokeWidth={isHi ? 2.2 : 1.5}
              strokeDasharray={seam.bridge ? undefined : "5 4"}
              markerEnd="url(#seammap-arrow)"
              style={{ transition: "all 160ms ease" }}
            />

            {/* Bridge node in 𝓔_I */}
            {seam.bridge ? (
              <>
                <rect
                  x={cx - 70}
                  y={totalY - 10}
                  width={140}
                  height={44}
                  rx={10}
                  fill={isHi ? `${accent}28` : "rgba(6,8,16,0.7)"}
                  stroke={accent}
                  strokeOpacity={isHi ? 0.9 : 0.55}
                  strokeWidth={isHi ? 1.8 : 1}
                  style={{ transition: "all 160ms ease" }}
                />
                <text
                  x={cx}
                  y={totalY + 8}
                  textAnchor="middle"
                  fill={panelTokens.bright}
                  fontSize={11.5}
                  fontFamily={panelTokens.mono}
                  fontWeight={700}
                >
                  {bridgeLabel.split(" ")[0]}
                </text>
                <text
                  x={cx}
                  y={totalY + 24}
                  textAnchor="middle"
                  fill={panelTokens.muted}
                  fontSize={9.5}
                  fontFamily={panelTokens.mono}
                >
                  {seam.bridge.id}
                </text>
              </>
            ) : (
              <>
                <rect
                  x={cx - 70}
                  y={totalY - 10}
                  width={140}
                  height={44}
                  rx={10}
                  fill="rgba(239,68,68,0.10)"
                  stroke={panelTokens.accentRed}
                  strokeOpacity={isHi ? 0.9 : 0.55}
                  strokeWidth={isHi ? 1.8 : 1}
                  style={{ transition: "all 160ms ease" }}
                />
                <text
                  x={cx}
                  y={totalY + 8}
                  textAnchor="middle"
                  fill={panelTokens.accentRed}
                  fontSize={11.5}
                  fontFamily={panelTokens.mono}
                  fontWeight={700}
                >
                  no bridge
                </text>
                <text
                  x={cx}
                  y={totalY + 24}
                  textAnchor="middle"
                  fill={panelTokens.muted}
                  fontSize={9.5}
                  fontFamily={panelTokens.mono}
                >
                  unknown ⇒ refuse
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}
